import * as angular from 'angular';
import * as _ from 'lodash';

import { ICategoricalChartOptions } from '../../../wtFramework';
import { groupSelected, refreshClick } from '../../../common/';
import { ComponentDefinition, TagDateValue, AggregationMode, WidgetValues, IBindable, Unit, IValueViewModel, RefreshClickData } from '../../../models';
import { mapSeries } from '../mapSeries';
import { ChartByIdentifierWidget } from '../chartByIdentifier.widget';
import { IDateRangeService, IValuesService, IwidgetIntervalHelperService, ISeriesService, IUnitService, IChartByIdentifierService, IExportToCsvService } from '../../../services';

class ChartByIdentifierController implements angular.IComponentController {
    data: TagDateValue[] = [];
    isLoading: boolean;
    options: ICategoricalChartOptions;
    series: any[] = [];

    readonly boundData: WidgetValues;
    readonly boundSeries: any[] = [];
    readonly isBound: boolean;
    readonly item: ChartByIdentifierWidget & { map: { [guid: string]: string } };
    readonly ratio: number;

    private _seriesConfiguration: IBindable[];
    private _unsubscribe: () => void;

    static $inject = [
        '$rootScope',
        '$scope',
        'dateRangeService',
        'exportToCsvService',
        'valuesService',
        'widgetIntervalHelperService',
        'seriesService',
        'unitService',
        'chartByIdentifierService'
    ];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$scope: angular.IScope,
        private _dateRangeService: IDateRangeService,
        private _exportToCsvService: IExportToCsvService,
        private _valuesService: IValuesService,
        private _intervalHelper: IwidgetIntervalHelperService,
        private _seriesService: ISeriesService,
        private _unitService: IUnitService,
        private _cbis: IChartByIdentifierService
    ) {
    }

    $onInit() {
        const dataFormat = {
            label: d => d.builtName,
            identifier: d => d.valueType + d.guid,
            value: d => d.value
        };
        if (this.item && this.item.configuration) {
            const opt = this.item.configuration.options as any;
            opt.dataFormat = dataFormat;
            this.options = opt;
            this.item.exportToCsv = () => this._exportToCsvService.exportAggregatedToCsv(this.series, this.boundData.aggregatedValue, this.item.name);
        }
        if (!this.isBound) {
            this._$scope.$watch(() => this.item, () => {
                if (this.item) {
                    this._$scope.$watch(() => this.item.configuration.options, () => {
                        const opt = this.item.configuration.options as any;
                        opt.dataFormat = dataFormat;
                        this.options = opt;
                        this.refreshSeries(this.item.configuration.series);
                    }, true);

                    this._$scope.$watch(() => this.item.configuration.nameConfiguration, (newValue) => {
                        this.refreshSeries(this.item.configuration.series);
                    }, true);

                    this._$scope.$watch(() => this.item.configuration.series, (newValue) => {
                        if (newValue) {
                            this.refreshSeries(newValue);
                        }
                    }, true);

                    this.refreshSeries(this.item.configuration.series);
                }
            });
            this._$scope.$on(groupSelected, (_e, _d) => {
                this.refreshSeries(this.item.configuration.series);
            });
        }
        if (this.boundData) {
            this._$scope.$watch(() => this.boundData, () => {
                this.data = this.boundData.aggregatedValue;
                this.series = this.mapSeries(this.boundSeries, this.data);
            });
        } else {
            this._$scope.$on(refreshClick, (ev, data: RefreshClickData) => {
                this.refresh(data.force);
            });
        }

        this._unsubscribe = this._$rootScope.$on('$translateChangeSuccess', () => {
            this.refreshSeries(this.item.configuration.series);
        });
    }

    $onChanges(changesObj) {
        if (changesObj['boundSeries']?.currentValue || changesObj['boundData']?.currentValue) {
            const data = (this.boundData || { aggregatedValue: [] }).aggregatedValue;
            this.data = data;
            this.series = this.mapSeries(this.boundSeries, data);
        }
    }

    $onDestroy() {
        this._unsubscribe();
    }

    private refreshSeries(newValue) {
        if (newValue) {
            this.isLoading = true;
            const unitName: string = undefined;
            this._unitService.getAll()
                .then(data => this._seriesService.getUpdatedGuids(newValue))
                .then(data => {
                    this._seriesConfiguration = data;
                    return this._cbis.getSeries(data as any[], this.item.configuration.nameConfiguration, unitName);
                })
                .then(data => {
                    const { series } = data;
                    this.series = this.mapSeries(series, this.data);
                    this.item.setError(data.error);
                    this.isLoading = false;
                    if (!this.boundData) {
                        this.refresh();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    this.isLoading = false;
                });
        }
    }

    private mapSeries(series: any[], data: TagDateValue[]) {
        return mapSeries(series, data, this.item, this.options);
    }

    private refresh(force?: boolean): any {
        if (this._dateRangeService.initialized() && this._seriesConfiguration && this._seriesConfiguration.length
            > 0) {
            this.isLoading = true;
            let beginDate = this._dateRangeService.beginDate() as string;
            let endDate = this._dateRangeService.endDate() as string;
            const updatedInterval = this._intervalHelper.getInterval(beginDate as string, endDate as string, this.item.configuration);
            beginDate = updatedInterval.beginMoment.toISOString();
            endDate = updatedInterval.endMoment.toISOString();
            let aggregationMode = AggregationMode.Average;
            if (this.item.configuration.aggregation) {
                aggregationMode = this.item.configuration.aggregation.mode;
            }
            this._valuesService
                .getValueBetweenDates(beginDate, endDate,
                    aggregationMode,
                    _.sortBy(this._seriesConfiguration, s => s.guid).map(d => {
                        return {
                            guid: d.guid,
                            value: d.value ? d.value : 'average'
                        };
                    }),
                    force)
                .then(data => {
                    this.data = data.values;
                    this.series = this.mapSeries(this.series, this.data);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    }

    getLabel(object) {
        let label = object.name;

        if (object.unitName) {
            label += ` [${object.unitName}]`;
        }

        return label;
    }
}

export const wtChartByIdentifier: ComponentDefinition = {
    componentId: 'wtChartByIdentifier',
    controller: ChartByIdentifierController,
    template: require('./chartByIdentifier.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<?',
        boundSeries: '<?',
        isBound: '<?',
        item: '<',
        ratio: '<?'
    }
};